import { useContext } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import AdminRoutes from "./pages/admin/AdminRoutes";
import ClubRoutes from "./pages/club/ClubRoutes";
import PrintRequests, { PrintRequestForm } from "./pages/printcard/PrintRequests";
import Users, { UserForm } from "./pages/users/Users";
import { firstAllowedPath, isOwner, STORE_MENU_CATALOG } from "./include/staffAccess";

/* ===================== AUTH ===================== */
import Login from './pages/Login/Login';
import ResetPassword from "./pages/Login/ResetPassword";
import StoreRegister from "./pages/store/StoreRegister";
import TicketIssuedPublic from "./pages/store/TicketIssuedPublic";
import DrawTicketBuy from "./pages/store/DrawTicketBuy";
import DrawTicketSuccess from "./pages/store/DrawTicketSuccess";
import DrawTicketFailure from "./pages/store/DrawTicketFailure";

/* ===================== COMMON ===================== */
import StoreSuccess from "./pages/gateway/StoreSuccess";
import StoreFailure from "./pages/gateway/StoreFailure";
import StoreResSuccess from './pages/gateway/StoreResSuccess';
import StoreResFailure from './pages/gateway/StoreResFailure';

import CustSuccess from "./pages/gateway/CustSuccess";
import CustFailure from "./pages/gateway/CustFailure";
import CustResSuccess  from './pages/gateway/CustResSuccess';
import CustResFailure  from './pages/gateway/CustResFailure';

import StoreShell from "./pages/Header/StoreShell";

import Dashboard        from "./pages/dashboard/Home";
import DashboardPlus    from "./pages/dashboard/HomePlus";
import LoyaltyDashboard from "./pages/dashboard/LoyaltyDashboard";

import CouponList   from "./pages/offer/CouponList";
import CreateCoupon from "./pages/offer/CreateCoupon";

import PrintPhysicalCard    from "./pages/printcard/PhysicalLoyaltyCard";
import LuckyDrawCoupon      from "./pages/printcard/LuckyDrawCoupon";
import PrintPhysicalBack    from "./pages/printcard/PhysicalLoyaltyBack";
import PhysicalLoyaltyQr    from "./pages/printcard/PhysicalLoyaltyQr";
import LuckyDrawCouponBack  from "./pages/printcard/LuckyDrawCouponBack";
import LuckyDrawRedeem      from "./pages/printcard/LuckyDrawRedeem";
import LuckyDrawWinner      from "./pages/printcard/LuckyDrawWinner";
import LuckyDrawMaster, { StoreDrawForm } from "./pages/printcard/LuckyDrawMaster";
import LaunchDraw from "./pages/admin/LaunchDraw";
import AuditTrail from "./pages/admin/AuditTrail";
import LuckyDrawResult      from "./pages/printcard/LuckyDrawResult";
import LuckyDrawCouponQr    from "./pages/printcard/LuckyDrawCouponQr";

import Wallet           from "./pages/Wallet/Wallet";
import WalletPlus       from "./pages/Wallet/WalletPlus";

import IssuedList       from "./pages/report/IssuedList";
import RedeemedList     from "./pages/report/RedeemedList";
import CustomerList     from "./pages/report/CustomerList";
import CustomerHistory  from "./pages/report/CustomerHistory";
import CustPhysicalCardList from "./pages/report/CustPhysicalCardList";

import { TotalPlusCustomer }     from "./pages/customer/CashiPlusCustomerList";
import CashiCustomerReport  from "./pages/report/CashiCustomerReport";
import CashiSaleReport      from "./pages/report/CashiSaleReport";
import RegisterSale, { SaleHome } from "./pages/report/RegisterSale";

import StoreLoyaltySetting  from "./pages/store/LoyaltySetting";
import LotRedeemCustList    from "./pages/cashi_lot_qrcode/LotRedeemCustList";
import RedeemedCustHistory  from "./pages/cashi_lot_qrcode/LotQrRedeemHistory";
import CampaignList         from "./pages/campaign/CampaignList";
import CreatePromotion      from "./pages/promotion/CreatePromotion";

import DialogBox from "./components/ajax/DialogBox";

import QR from "./pages/PlusQrcode/QR";

import StoreReviewList from './pages/review/StoreReviewList';
import StoreReviewAdd  from './pages/review/StoreReviewAdd';
import SpinWheelList, { SpinWheelForm, SpinWheelPlay, SpinWheelScreen, SpinWinners } from "./pages/store/SpinWheel";
import SpinClaim from "./pages/store/SpinClaim";

/* ===================== DASHBOARD PLUS ===================== */
import GeneratedQR  from "./pages/cashi_lot_qrcode/GeneratedQR";


import LoadQR from "./components/DashboardPlus/LoadQR";
import GenerateQRCode from "./components/DashboardPlus/GenerateQRCode";
import PrintQRPlus from "./components/DashboardPlus/PrintQRPlus";

import LoadnActiveQR from "./components/DashboardPlus/LoadnActiveQR";

import { ActivatedQR } from "./components/DashboardPlus/ActivatedQR";
import {LoadedQR} from "./components/DashboardPlus/LoadedQR";
import { DataContext } from "./context/DataContext";

// ================================================= 
// ================== AUTH GUARD =================== 
// ================================================= 

const RequireAuth = ({ children }) => 
{
    const { loginData } = useContext(DataContext);
    const isAuthenticated = !!loginData?.store_info?.store_id;

    return isAuthenticated ? children : <Navigate to="/" replace />;
};

// ------------------------------------------------------------------
// MAIN DASHBOARD LAYOUT
// ------------------------------------------------------------------
const DashboardLayout = ({ onLogout }) => (
  <StoreShell mode="loyalty" onLogout={onLogout}>
      <Routes>
        <Route index              element={<Dashboard />} />
        <Route path="home"        element={<Dashboard />} />
        <Route path="dashboard"   element={<LoyaltyDashboard />} />
        
        <Route path="customerReport"  element={<CashiCustomerReport />} />
        <Route path="saleReport"      element={<CashiSaleReport />} />
        <Route path="registerSale"    element={<RegisterSale />} />
        <Route path="customerList/:customerId" element={<CustomerHistory />} />
        <Route path="customerList"    element={<CustomerList />} />
        <Route path="physicalCard"    element={<CustPhysicalCardList />} />
        <Route path="issuedList"      element={<IssuedList />} />
        <Route path="redeemedList"    element={<RedeemedList />} />
      
        <Route path="printphysicalcard/new" element={<PrintRequestForm source="store" />} />
        <Route path="printphysicalcard" element={<PrintRequests source="store" />} />
        <Route path="printphysicalcard/design" element={<PrintPhysicalCard />} />
        <Route path="printphysicalback" element={<PrintPhysicalBack />} />
        <Route path="printphysicalqr"   element={<PhysicalLoyaltyQr />} />
        
        <Route path="luckydrawcoupon" element={<LuckyDrawCoupon />} />
        <Route path="luckydrawcouponback" element={<LuckyDrawCouponBack />} />
        <Route path="luckydrawcouponQr" element={<LuckyDrawCouponQr />} />
        <Route path="luckydrawredeem" element={<LuckyDrawRedeem />} />
        <Route path="spinwin/new" element={<SpinWheelForm />} />
        <Route path="spinwin/:wheelId/edit" element={<SpinWheelForm />} />
        <Route path="spinwin/:wheelId/wheel" element={<SpinWheelPlay />} />
        <Route path="spinwin/:wheelId/winners" element={<SpinWinners />} />
        <Route path="spinwin" element={<SpinWheelList />} />
        <Route path="luckydraws/:drawId/launch" element={<LaunchDraw source="store" />} />
        <Route path="luckydraws/new" element={<StoreDrawForm />} />
        <Route path="luckydraws/:drawId/edit" element={<StoreDrawForm />} />
        <Route path="luckydraws" element={<LuckyDrawMaster />} />
        <Route path="luckydraw/:drawId" element={<LuckyDrawResult />} />
        <Route path="luckydrawwinner" element={<LuckyDrawWinner />} />

        <Route path="couponlist"      element={<CouponList />} />
        <Route path="createcoupon"    element={<CreateCoupon />} />
        <Route path="createpromotion" element={<CreatePromotion />} />

        <Route path="wallet" element={<Wallet />} />
        <Route path="audit" element={<AuditTrail source="store" />} />
        
        <Route path="campaignList" element={<CampaignList />} />

        <Route path="reviewList" element={<StoreReviewList />} />

        
        <Route path="loyaltysetting" element={<StoreLoyaltySetting />} />
        <Route path="users/new" element={<UserForm source="store" />} />
        <Route path="users/:userId/edit" element={<UserForm source="store" />} />
        <Route path="users" element={<Users source="store" />} />

        <Route path="dialogbox" element={<DialogBox />} />
        <Route path="success" element={<StoreSuccess />} />
        <Route path="failure" element={<StoreFailure />} />
      </Routes>
  </StoreShell>
);

// ------------------------------------------------------------------
// DASHBOARD PLUS LAYOUT
// ------------------------------------------------------------------
const DashboardPlusLayout = ({ onLogout }) => (
  <StoreShell mode="plus" onLogout={onLogout}>
      <Routes>
        <Route index element={<DashboardPlus />} />
        <Route path="walletPlus"  element={<WalletPlus />} />

        <Route path="spinwin/new" element={<SpinWheelForm />} />
        <Route path="spinwin/:wheelId/edit" element={<SpinWheelForm />} />
        <Route path="spinwin/:wheelId/wheel" element={<SpinWheelPlay />} />
        <Route path="spinwin/:wheelId/winners" element={<SpinWinners />} />
        <Route path="spinwin" element={<SpinWheelList />} />
        <Route path="generatedQR" element={<GeneratedQR />} />
        <Route path="activatedQR" element={<ActivatedQR />} />
        <Route path="loadedQR" element={<LoadedQR />} />
        <Route path="loadnactiveqr" element={<LoadnActiveQR />} />

        <Route path="totalPlusCustomer" element={<TotalPlusCustomer />} />
        <Route path="generateQR" element={<GenerateQRCode />} />
        <Route path="qr" element={<QR />} />
        <Route path="printQR" element={<PrintQRPlus />} />
        <Route path="loadQR" element={<LoadQR />} />
        <Route path="users/new" element={<UserForm source="store" />} />
        <Route path="users/:userId/edit" element={<UserForm source="store" />} />
        <Route path="users" element={<Users source="store" />} />

        
        <Route path="redeemed"    element={<LotRedeemCustList />} />
        <Route path="redeemed/redeemedcusthistory" element={<RedeemedCustHistory />} />

        <Route path="success" element={<StoreSuccess />} />
        <Route path="failure" element={<StoreFailure />} />
      </Routes>
  </StoreShell>
);

/* ================================================= */
/* ================= PUBLIC ROUTES ================= */
/* ================================================= */

const PublicRoutes = ({ onLogin }) => (
  <Routes>
    <Route path="/" element={<Login onLogin={onLogin} />} />
    <Route path="/storeregister" element={<StoreRegister />} />
    <Route path="/resetpassword" element={<ResetPassword />} />
    <Route path="/drawticket/success" element={<DrawTicketSuccess />} />
    <Route path="/drawticket/failure" element={<DrawTicketFailure />} />
    <Route path="/drawticket/:ticketId" element={<DrawTicketBuy />} />
    <Route path="/store_pay_sucsess" element={<StoreResSuccess />} />
    <Route path="/store_pay_failure" element={<StoreResFailure />} />
    <Route path="/cust_pay_sucsess"  element={<CustResSuccess />} />
    <Route path="/cust_pay_failure"  element={<CustResFailure />} />   
    <Route path="csuccess"           element={<CustSuccess />} />
    <Route path="cfailure"           element={<CustFailure />} /> 
    <Route path="/reviewadd"         element={<StoreReviewAdd />} />
    <Route path="/spin/:claimId"      element={<SpinClaim />} />
    <Route path="/spinwheel/:token"   element={<SpinWheelScreen />} />
    <Route path="/ticketissued/:token" element={<TicketIssuedPublic />} />
    <Route path="/launch/:token" element={<LaunchDraw source="public" />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// ------------------------------------------------------------------
// APP ROUTES
// ------------------------------------------------------------------
const AppRoutes = () => 
{
    const navigate = useNavigate();
    const location = useLocation();
    const { loginData, setLoginData } = useContext(DataContext);

    if (location.pathname.startsWith("/ticketissued")) {
        return (
            <Routes>
                <Route path="/ticketissued/:token" element={<TicketIssuedPublic />} />
            </Routes>
        );
    }

    if (location.pathname === "/console" || location.pathname.startsWith("/console/")) {
        return <AdminRoutes />;
    }

    if (location.pathname === "/club" || location.pathname.startsWith("/club/")) {
        return <ClubRoutes />;
    }

    if (location.pathname === "/sale" || location.pathname === "/sale/" || location.pathname.startsWith("/sale/")) {
        return (
            <Routes>
                <Route path="/sale" element={<SaleHome />} />
                <Route path="/sale/:token" element={<RegisterSale />} />
            </Routes>
        );
    }

    if (location.pathname.startsWith("/launch/")) {
        return (
            <Routes>
                <Route path="/launch/:token" element={<LaunchDraw source="public" />} />
            </Routes>
        );
    }

    const isAuthenticated = !!loginData?.store_info?.store_id;
    const cashiPlusFlag = loginData?.store_info?.cashi_plus_flag;

    /* ---------------- LOGIN ---------------- */
    const handleLogin = (userData) => 
    {
        // ✅ Update Context
        const formattedUser = { ...userData,store_info: userData.store_info};
        setLoginData(formattedUser);

        const cashiPlusFlag = userData?.store_info?.cashi_plus_flag;
        const fallback = cashiPlusFlag === "2" ? "/dashboardPlus" : "/home";
        if (isOwner(userData)) {
          navigate(fallback);
          return;
        }
        const catalogGroups = [{ items: STORE_MENU_CATALOG.map((item) => ({ to: item.paths[0], menu: item.id })) }];
        navigate(firstAllowedPath(userData, catalogGroups, fallback));

    };

  /* ---------------- LOGOUT ---------------- */
  const handleLogout = () => 
  {
      sessionStorage.clear();
      setLoginData({});
      navigate("/", { replace: true });
  };

  if (!isAuthenticated) 
  {
    return <PublicRoutes onLogin={handleLogin} />;
  }

  return (
    <Routes>
      <Route path="/store_pay_sucsess" element={<StoreResSuccess />} />
      <Route path="/store_pay_failure" element={<StoreResFailure />} />
      <Route path="/storeregister" element={<StoreRegister />} />
      <Route path="/resetpassword" element={<ResetPassword />} />
      <Route path="/drawticket/success" element={<DrawTicketSuccess />} />
      <Route path="/drawticket/failure" element={<DrawTicketFailure />} />
      <Route path="/drawticket/:ticketId" element={<DrawTicketBuy />} />
      <Route path="/spin/:claimId" element={<SpinClaim />} />
      <Route path="/spinwheel/:token" element={<SpinWheelScreen />} />
      <Route path="/ticketissued/:token" element={<TicketIssuedPublic />} />
      <Route path="/launch/:token" element={<LaunchDraw source="public" />} />

      {/* PLUS DASHBOARD */}
        <Route path="/dashboardPlus/*" element={ <RequireAuth> 
            {(cashiPlusFlag === "1" || cashiPlusFlag === "2") ? (<DashboardPlusLayout onLogout={handleLogout} /> ) : (<Navigate to="/home" replace />)} </RequireAuth>} />

      {/* NORMAL DASHBOARD */}
          <Route path="/*" element={ <RequireAuth> 
              {cashiPlusFlag === "2" ? ( <Navigate to="/dashboardPlus" replace /> ) : ( <DashboardLayout onLogout={handleLogout} /> )} </RequireAuth> }/>

    </Routes>
  );
};

export default AppRoutes;
